import { useState } from "react";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  SvgIcon,
  TableRow,
  Typography,
  LinearProgress,
  TablePagination,
  TextField,
  InputAdornment,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit.svg";
import DeleteIcon from "@mui/icons-material/Delete";
import NewSubjectForm from "./NewSubjectForm";
import EditSubjectForm from "./EditSubjectForm";
import {
  useDeleteSubjectMutation,
  useGetSubjectsPaginatedQuery,
} from "./subjectsApiSlice";
import toast from "../../../helpers/toast";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";

function Subjects() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openEdit, setOpenEdit] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [focusedSubject, setFocusedSubject] = useState(null);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: subjectsData, isLoading } = useGetSubjectsPaginatedQuery({
    page: page + 1,
    per_page: perPage,
    q: searchQuery,
  });
  const subjects = subjectsData?.subjects ?? [];
  const totalCount = subjectsData?.pagination?.total_items ?? 0;

  const [deleteSubject] = useDeleteSubjectMutation();

  const handleDelete = async (subject) => {
    try {
      const response = await deleteSubject(subject).unwrap();
      if (response) {
        toast.success("Subject deleted successfully");
      }
    } catch (error) {
      if (error.status === 404) {
        toast.success("Subject deleted successfully");
      }
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <CustomWarningDialog
          open={openWarning}
          setOpen={setOpenWarning}
          action={handleDelete}
          item={deleteItem}
          message="Are you sure you want to delete this subject?"
        />

        <Grid item xs={12} md={12} lg={6}>
          <StyledPaper>
            {openEdit ? (
              <EditSubjectForm
                focusedSubject={focusedSubject}
                setFocusedSubject={setFocusedSubject}
                setOpenEdit={setOpenEdit}
              />
            ) : (
              <NewSubjectForm
                setFocusedSubject={setFocusedSubject}
                setOpenEdit={setOpenEdit}
              />
            )}
          </StyledPaper>
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <StyledPaper>
            <Box className="paperHeader">
              <Typography variant="formHeading">Subjects</Typography>
              <Typography
                variant="formSubHeading"
                sx={{ textTransform: "none" }}
              >
                To modify subject info, click edit icon
              </Typography>
            </Box>
            <Box sx={{ px: 2, pb: 1 }}>
              <TextField
                size="small"
                placeholder="Search subjects..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Box>
            <Box className="e">
              {isLoading && <LinearProgress />}
              {isMobile ? (
                <Box sx={{ p: 2 }}>
                  {subjects?.map((subject) => (
                    <Box
                      key={subject.id}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        sx={{ color: "primary.main", fontWeight: 600 }}
                      >
                        {subject.name}
                      </Typography>
                      {/* <Typography variant="body2" color="text.secondary">{subject.description || "-"}</Typography> */}
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <SvgIcon
                          sx={{
                            color: "primary.safeOnLight",
                            cursor: "pointer",
                          }}
                          component={EditIcon}
                          inheritViewBox
                          onClick={() => {
                            setFocusedSubject(subject);
                            setOpenEdit(true);
                          }}
                        />
                        <DeleteIcon
                          className="tableCellLink"
                          onClick={() => {
                            setDeleteItem(subject);
                            setOpenWarning(true);
                          }}
                        />
                      </Stack>
                    </Box>
                  ))}
                </Box>
              ) : (
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table sx={{ width: "100%", boxShadow: "none" }}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Subject </StyledTableCell>
                        {/* <StyledTableCell align="left">
                          description
                        </StyledTableCell> */}
                        <StyledTableCell align="right">edit</StyledTableCell>
                        <StyledTableCell align="left">delete</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subjects?.map((subject) => (
                        <StyledTableRow key={subject.id}>
                          <StyledTableCell component="th" scope="row">
                            {subject.name}
                          </StyledTableCell>
                          {/* <StyledTableCell align="left">
                            {subject.description}
                          </StyledTableCell> */}
                          <StyledTableCell align="right">
                            <SvgIcon
                              sx={{
                                color: "primary.safeOnLight",
                                cursor: "pointer",
                              }}
                              component={EditIcon}
                              inheritViewBox
                              onClick={() => {
                                setFocusedSubject(subject);
                                setOpenEdit(true);
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <DeleteIcon
                              className="tableCellLink"
                              onClick={() => {
                                setDeleteItem(subject);
                                setOpenWarning(true);
                              }}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={perPage}
                onRowsPerPageChange={(e) => {
                  setPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                sx={{
                  ".MuiTablePagination-toolbar": {
                    flexWrap: "wrap",
                    minWidth: 0,
                    px: 0,
                  },
                  ".MuiTablePagination-spacer": { display: "none" },
                }}
              />
            </Box>
          </StyledPaper>
        </Grid>
      </Grid>
    </>
  );
}

export default Subjects;
